import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import BracketViewer from './BracketViewer';
import BracketAdmin from './BracketAdmin';
import LeagueQRScanner from './LeagueQRScanner';
import './styles/index.css';
import './styles/league-access.css';
import DashboardHeader from './DashboardHeader';

const TOURNAMENT_RESUME_VIEW_KEY = 'dartleague-tournament-resume-view';

const TOURNAMENT_QR_CONTENT = {
  heroEyebrow: 'Entrar a campeonato',
  heroTitle: 'Valida tu acceso desde el movil',
  heroDescription: 'Usa la camara del navegador para escanear el QR de la tablet del bar y confirmar tu acceso a la partida del campeonato.',
  idleEyebrow: 'Acceso QR de campeonato',
  idleTitle: 'Escanea el QR de la tablet',
  idleDescription: 'Pulsa en abrir camara y apunta al QR que muestra la tablet del bar para validar tu acceso al campeonato en segundos.',
  steps: ['1. Abre camara', '2. Enfoca la tablet', '3. Valida tu acceso'],
  backLabel: 'Volver a campeonatos',
  successTitle: 'Validacion enviada',
  compactNote: 'Consejo: acerca la tablet al movil, sube su brillo y evita reflejos. Si ves la camara equivocada, pulsa en cambiar camara.',
};

const getInitialTournamentView = () => {
  if (typeof window === 'undefined') {
    return 'overview';
  }

  const storedView = sessionStorage.getItem(TOURNAMENT_RESUME_VIEW_KEY);
  sessionStorage.removeItem(TOURNAMENT_RESUME_VIEW_KEY);

  return storedView === 'play' ? 'play' : 'overview';
};

const buildTournamentSuccessMessage = (result) => {
  const barName = result?.barInfo?.name ? ` de ${result.barInfo.name}` : '';
  return `La tablet${barName} ya ha recibido tu acceso para la partida del campeonato.`;
};

const EMPTY_TOURNAMENT_QR_STATE = {
  isEnabled: false,
  title: 'Disponible cuando haya campeonatos',
  description: 'Cuando se abra un campeonato para ti, aqui podras escanear el QR de la tablet del bar para validar tu acceso a la partida.',
  buttonLabel: 'No disponible',
  buttonDescription: 'Sin campeonatos',
};

const getTournamentQrState = (campeonato, isBarUser) => {
  if (isBarUser || !campeonato) {
    return null;
  }

  if (campeonato._usuarioParticipa && campeonato.estado !== 'finalizado') {
    return {
      isEnabled: true,
      title: 'Entra a tu partida desde aqui',
      description: 'Escanea el QR que aparece en la tablet del bar y valida tu acceso al campeonato en segundos.',
      buttonLabel: 'Entrar con QR',
      buttonDescription: 'Abrir camara',
    };
  }

  if (campeonato.estado === 'finalizado') {
    return {
      isEnabled: false,
      title: 'Acceso QR ya cerrado',
      description: 'Este campeonato ya ha terminado. El acceso QR solo se habilita mientras hay partidas activas.',
      buttonLabel: 'No disponible',
      buttonDescription: 'Campeonato finalizado',
    };
  }

  return {
    isEnabled: false,
    title: 'Disponible cuando participes',
    description: 'Ahora mismo estas viendo este campeonato en modo espectador. El acceso QR se activa cuando participas en una partida de este campeonato.',
    buttonLabel: 'No disponible',
    buttonDescription: 'Modo espectador',
  };
};

const TorneosDashboard = ({ onBack, onTournamentViewChange }) => {
  const [user] = useState(api.getCurrentUser());
  const [campeonatos, setCampeonatos] = useState([]);
  const [selectedCampeonato, setSelectedCampeonato] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('dartleague-selected-campeonato');
  });
  const [bracketData, setBracketData] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingBracket, setLoadingBracket] = useState(false);
  const [promedioGlobal, setPromedioGlobal] = useState({ promedioTotal: 0, porcentajeVictorias: 0, promedioOnline: null });
  const [loadingGlobal, setLoadingGlobal] = useState(true);
  const [error, setError] = useState('');
  const [tournamentView, setTournamentView] = useState(() => getInitialTournamentView());
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'bracket';
    return localStorage.getItem('dartleague-active-tab') || 'bracket';
  }); // 'bracket', 'ranking'
  
  // Detectar si es un bar (admin)
  const esBar = user?.tipoUsuario === 'bar';
  const campeonatoActual = campeonatos.find((campeonato) => campeonato._id === selectedCampeonato);
  const tournamentQrState = getTournamentQrState(campeonatoActual, esBar);
  const showTournamentQrCard = Boolean(tournamentQrState);
  const canUseTournamentQrAccess = Boolean(tournamentQrState?.isEnabled);

  useEffect(() => {
    onTournamentViewChange?.(tournamentView);
  }, [tournamentView, onTournamentViewChange]);

  useEffect(() => {
    if (!canUseTournamentQrAccess && tournamentView !== 'overview') {
      setTournamentView('overview');
    }
  }, [canUseTournamentQrAccess, tournamentView]);

  // Cargar promedio global del jugador
  const loadPromedioGlobal = useCallback(async () => {
    try {
      setLoadingGlobal(true);
      const [data, onlineStats] = await Promise.all([
        api.getPromedioTotal(),
        api.getOnlineStats()
      ]);
      setPromedioGlobal({
        promedioTotal: data.promedioTotal || 0,
        porcentajeVictorias: data.porcentajeVictorias || 0,
        promedioOnline: onlineStats?.promedioOnline ?? null
      });
    } catch (err) {
      console.error('Error cargando promedio global:', err);
      setPromedioGlobal({ promedioTotal: 0, porcentajeVictorias: 0, promedioOnline: null });
    } finally {
      setLoadingGlobal(false);
    }
  }, []);

  const loadCampeonatoData = useCallback(async () => {
    try {
      setLoadingBracket(true);
      const [bracket, rankingData] = await Promise.all([
        api.getBracket(selectedCampeonato),
        api.getRanking(selectedCampeonato)
      ]);
      
      setBracketData(bracket);
      setRanking(rankingData);
    } catch (err) {
      setError('Error cargando datos del campeonato');
      console.error(err);
    } finally {
      setLoadingBracket(false);
    }
  }, [selectedCampeonato]);

  useEffect(() => {
    loadCampeonatos();
    loadPromedioGlobal();
  }, [loadPromedioGlobal]);

  useEffect(() => {
    if (selectedCampeonato) {
      loadCampeonatoData();
    }
  }, [selectedCampeonato, loadCampeonatoData]);

  // Persistir selección y pestaña para mantener la vista tras un reload
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (selectedCampeonato) {
      localStorage.setItem('dartleague-selected-campeonato', selectedCampeonato);
    }
  }, [selectedCampeonato]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('dartleague-active-tab', activeTab);
  }, [activeTab]);

  const loadCampeonatos = async () => {
    try {
      setLoading(true);
      // Cargar TODOS los campeonatos (no solo los del usuario)
      const data = await api.getAllCampeonatos();
      setCampeonatos(data);
      
      if (data.length > 0) {
        const stored = typeof window !== 'undefined' ? localStorage.getItem('dartleague-selected-campeonato') : null;
        const existeStored = stored && data.some(c => c._id === stored);
        
        if (existeStored) {
          setSelectedCampeonato(stored);
        } else {
          // Por defecto, seleccionar el primero (que será uno donde participa si existe)
          setSelectedCampeonato(data[0]._id);
        }
      }
    } catch (err) {
      setError('Error cargando campeonatos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Navegación entre campeonatos
  const getCurrentIndex = () => {
    return campeonatos.findIndex(c => c._id === selectedCampeonato);
  };

  const goToPrevCampeonato = () => {
    const currentIndex = getCurrentIndex();
    if (currentIndex > 0) {
      setSelectedCampeonato(campeonatos[currentIndex - 1]._id);
    }
  };

  const goToNextCampeonato = () => {
    const currentIndex = getCurrentIndex();
    if (currentIndex < campeonatos.length - 1) {
      setSelectedCampeonato(campeonatos[currentIndex + 1]._id);
    }
  };

  const handleLogout = () => {
    api.logout();
    window.location.reload();
  };

  const renderTournamentQrEntry = (state) => {
    if (!state) {
      return null;
    }

    const isEnabled = Boolean(state.isEnabled);

    return (
      <div className={`league-access-entry${isEnabled ? '' : ' is-disabled'}`}>
        <div className="league-access-copy">
          <span className="league-qr-eyebrow">Acceso QR de campeonato</span>
          <h3>{state.title}</h3>
          <p>{state.description}</p>
        </div>

        <button
          className={`league-access-trigger${isEnabled ? '' : ' is-disabled'}`}
          onClick={isEnabled ? () => setTournamentView('play') : undefined}
          disabled={!isEnabled}
          aria-disabled={!isEnabled}
        >
          <span className="league-access-trigger-copy">
            <span className="league-view-btn-icon" aria-hidden="true">
              <span className="league-view-btn-icon-core" />
            </span>
            <span className="league-access-trigger-text">
              <span className="league-view-btn-label">{state.buttonLabel}</span>
              <span className="league-view-btn-description">{state.buttonDescription}</span>
            </span>
          </span>

          <span className="league-access-trigger-visual" aria-hidden="true">
            <span className="league-access-visual-grid" />
            <span className="league-access-scanframe feature">
              <span className="league-access-scanframe-core" />
              <span className="league-access-corner top-left" />
              <span className="league-access-corner top-right" />
              <span className="league-access-corner bottom-left" />
              <span className="league-access-corner bottom-right" />
              <span className="league-access-scanline" />
            </span>
          </span>
        </button>
      </div>
    );
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 'configuracion': return 'En Configuración';
      case 'bracket_generado': return 'Bracket Listo';
      case 'en_curso': return 'En Curso';
      case 'finalizado': return 'Finalizado';
      default: return estado;
    }
  };

  if (loading) {
    return (
      <div className="dart-league-dashboard">
        <div className="loading">⏳ Cargando torneos...</div>
      </div>
    );
  }

  if (campeonatos.length === 0) {
    return (
      <div className="dart-league-dashboard">
        <DashboardHeader
          user={user}
          subtitle="Campeonatos"
          loading={loadingGlobal}
          promedioTotal={promedioGlobal.promedioTotal}
          porcentajeVictorias={promedioGlobal.porcentajeVictorias}
          onBack={onBack}
          onLogout={handleLogout}
        />

        {!esBar && renderTournamentQrEntry(EMPTY_TOURNAMENT_QR_STATE)}

        {/* Mensaje vacío */}
        <div className="empty-state">
          <h2>🏆 No hay torneos disponibles</h2>
          <p>Aún no se han creado torneos.</p>
          <div className="empty-state-actions">
            {onBack && (
              <button className="torneos-button" onClick={onBack}>
                ← Volver al inicio
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dart-league-dashboard">
      <DashboardHeader
        user={user}
        subtitle="Campeonatos"
        loading={loadingGlobal}
        promedioTotal={promedioGlobal.promedioTotal}
        porcentajeVictorias={promedioGlobal.porcentajeVictorias}
        onBack={onBack}
        onLogout={handleLogout}
      />

      {error && (
        <div className="error-banner">⚠️ {error}</div>
      )}

      {tournamentView === 'overview' && showTournamentQrCard && (
        renderTournamentQrEntry(tournamentQrState)
      )}

      {tournamentView === 'play' && canUseTournamentQrAccess ? (
        <LeagueQRScanner
          onBack={() => setTournamentView('overview')}
          content={TOURNAMENT_QR_CONTENT}
          successMessageBuilder={buildTournamentSuccessMessage}
        />
      ) : (
        <>

      {/* Navegador de campeonatos con flechas grandes */}
      {campeonatos.length > 1 && (
        <div className="campeonato-navigator">
          <button 
            className="nav-arrow nav-arrow-left"
            onClick={goToPrevCampeonato}
            disabled={getCurrentIndex() === 0}
            title="Torneo anterior"
          >
            ◀
          </button>
          
          <div className="nav-info">
            <span className="nav-counter">
              Cambiar Campeonato ({getCurrentIndex() + 1}/{campeonatos.length})
            </span>
            <select 
              value={selectedCampeonato} 
              onChange={(e) => setSelectedCampeonato(e.target.value)}
              className="nav-select"
            >
              {campeonatos.map((camp, idx) => (
                <option key={camp._id} value={camp._id}>
                  {camp._usuarioParticipa ? '🏆 ' : ''}{camp.modalidad === 'parejas' ? '👥 ' : ''}{camp.nombre}
                </option>
              ))}
            </select>
            {campeonatoActual && !campeonatoActual._usuarioParticipa && (
              <span className="nav-badge-spectator">👁️ Espectador</span>
            )}
            {campeonatoActual && campeonatoActual._usuarioParticipa && (
              <span className="nav-badge-participant">⭐ Participante</span>
            )}
            {campeonatoActual && campeonatoActual.modalidad === 'parejas' && (
              <span className="nav-badge-parejas">👥 Parejas</span>
            )}
          </div>
          
          <button 
            className="nav-arrow nav-arrow-right"
            onClick={goToNextCampeonato}
            disabled={getCurrentIndex() === campeonatos.length - 1}
            title="Torneo siguiente"
          >
            ▶
          </button>
        </div>
      )}

      {/* Indicador cuando solo hay un campeonato */}
      {campeonatos.length === 1 && campeonatoActual && (
        <div className="campeonato-single-indicator">
          {campeonatoActual._usuarioParticipa ? (
            <span className="nav-badge-participant">⭐ Participante</span>
          ) : (
            <span className="nav-badge-spectator">👁️ Modo Espectador</span>
          )}
        </div>
      )}

      {/* Info del campeonato */}
      {campeonatoActual && (
        <div className="campeonato-hero">
          <div className="campeonato-hero-header">
            <div className="campeonato-titulo-container">
              <span className="campeonato-icon">🏆</span>
              <div className="campeonato-titulo-info">
                <h2 className="campeonato-nombre">{campeonatoActual.nombre}</h2>
                <span className={`estado-badge-nuevo ${campeonatoActual.estado}`}>
                  <span className="estado-dot"></span>
                  {getEstadoTexto(campeonatoActual.estado)}
                </span>
              </div>
            </div>
          </div>
          
          <div className="campeonato-stats-grid">
            <div className="stat-card-nuevo">
              <div className="stat-icon">{campeonatoActual.modalidad === 'parejas' ? '👥' : '👤'}</div>
              <div className="stat-content">
                <span className="stat-numero">
                  {campeonatoActual.modalidad === 'parejas' 
                    ? `${new Set(campeonatoActual.participantes?.map(p => p.equipoIndex)).size || 0}`
                    : campeonatoActual.participantes?.length || 0}
                  <span className="stat-separador">/</span>
                  {campeonatoActual.tipoBracket}
                </span>
                <span className="stat-texto">{campeonatoActual.modalidad === 'parejas' ? 'Equipos' : 'Jugadores'}</span>
              </div>
            </div>
            <div className="stat-card-nuevo">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <span className="stat-numero">{campeonatoActual.tipoEliminacion === 'simple' ? 'Simple' : 'Double'}</span>
                <span className="stat-texto">Eliminación</span>
              </div>
            </div>

          </div>

          {campeonatoActual.resultados?.campeon && (
            <div className="campeon-banner-nuevo">
              <span className="campeon-trophy">🏆</span>
              <div className="campeon-info">
                <span className="campeon-label">CAMPEÓN</span>
                <span className="campeon-nombre">{campeonatoActual.resultados.campeon.username || campeonatoActual.resultados.campeon.nombre}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className={`tab-btn ${activeTab === 'bracket' ? 'active' : ''}`}
          onClick={() => setActiveTab('bracket')}
        >
          🎯 Bracket
        </button>
        <button 
          className={`tab-btn ${activeTab === 'ranking' ? 'active' : ''}`}
          onClick={() => setActiveTab('ranking')}
        >
          📊 Ranking
        </button>
      </div>

      {/* Contenido */}
      {loadingBracket ? (
        <div className="loading-small">Cargando bracket...</div>
      ) : (
        <>
          {activeTab === 'bracket' && bracketData && (
            esBar ? (
              <BracketAdmin 
                bracketData={bracketData} 
                campeonatoId={selectedCampeonato}
                campeonatoNombre={campeonatoActual?.nombre}
                onUpdate={loadCampeonatoData}
              />
            ) : (
              <BracketViewer 
                bracketData={bracketData} 
                userId={user._id}
                campeonatoNombre={campeonatoActual?.nombre}
              />
            )
          )}

          {activeTab === 'ranking' && (
            <div className="section">
              <h2>🏆 Clasificación del Torneo</h2>
              <p className="ranking-info-text">Posiciones según resultado en el torneo</p>
              {ranking.length === 0 ? (
                <p className="empty-message">El ranking se mostrará cuando avance el torneo</p>
              ) : (
                <div className="table-container">
                  <table className="clasificacion-table">
                    <thead>
                      <tr>
                        <th>Pos</th>
                        <th>Jugador</th>
                        <th>Estado</th>
                        <th>Average</th>
                        <th>180s</th>
                        <th>V/P</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranking.map((item, index) => {
                        // Calcular posición visual (permite empates en misma posición)
                        const getPosVisual = (idx) => {
                          if (idx === 0) return 1;
                          const actual = ranking[idx];
                          const anterior = ranking[idx - 1];
                          if (actual.posicionFinal && anterior.posicionFinal && 
                              actual.posicionFinal === anterior.posicionFinal) {
                            return getPosVisual(idx - 1);
                          }
                          return idx + 1;
                        };
                        
                        const posVisual = getPosVisual(index);
                        const estado = item.posicionFinal 
                          ? `${item.posicionFinal}º` 
                          : '🎯 En juego';
                        const esActivo = !item.posicionFinal;
                        
                        return (
                          <tr 
                            key={item.id || index}
                            className={`${item.id === user._id ? 'my-row' : ''} ${posVisual <= 3 ? `top-${posVisual}` : ''} ${esActivo ? 'active-player-row' : ''}`}
                          >
                            <td className={`pos pos-${posVisual}`}>
                              {posVisual <= 3 ? (
                                <span className="medal-emoji">
                                  {posVisual === 1 ? '🥇' : posVisual === 2 ? '🥈' : '🥉'}
                                </span>
                              ) : (
                                posVisual
                              )}
                            </td>
                            <td className="jugador">
                              {item.nombre || 'Jugador'}
                              {item.id === user._id && ' 👤'}
                            </td>
                            <td className={`estado-col ${esActivo ? 'activo' : ''}`}>
                              {estado}
                            </td>
                            <td className="average-col">
                              <strong>{(item.averageTotal || 0).toFixed(2)}</strong>
                            </td>
                            <td>{item.total180s || 0}</td>
                            <td>{item.victorias || 0}/{item.partidas || 0}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
        </>
      )}
    </div>
  );
};

export default TorneosDashboard;
